import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import {
  useListAttributeValues,
  useCreateAttributeValue,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface AttributeValueComboboxProps {
  attributeKeyId: number | null;
  value: number | null;
  // 2026-05-21: second arg carries the value's display text so callers can store/show
  // it immediately without waiting for the values list to (re)load.
  onChange: (id: number, valueText?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  // 2026-05-21: raw text to display when the id hasn't resolved against the (async)
  // values list yet — e.g. an inherited/just-saved value. Prevents the "shows blank
  // until you reopen the modal" UX.
  valueText?: string;
}

export function AttributeValueCombobox({
  attributeKeyId,
  value,
  onChange,
  placeholder = "Select value…",
  disabled = false,
  valueText,
}: AttributeValueComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: values = [] } = useListAttributeValues(
    attributeKeyId != null ? { attributeKeyId, status: "Active" } : undefined,
    {
      query: {
        queryKey: ["listAttributeValues", attributeKeyId],
        enabled: attributeKeyId != null,
      },
    }
  );

  const createMutation = useCreateAttributeValue();

  const selectedValue = values.find((v) => v.id === value);

  const filteredValues = values.filter((v) =>
    v.value.toLowerCase().includes(inputValue.toLowerCase())
  );

  const exactMatch = values.some(
    (v) => v.value.toLowerCase() === inputValue.toLowerCase()
  );

  const showAddNew =
    inputValue.trim().length > 0 && !exactMatch && attributeKeyId != null;

  async function handleAddNew() {
    if (attributeKeyId == null) return;
    try {
      const res = await createMutation.mutateAsync({
        data: {
          attributeKeyId,
          value: inputValue.trim(),
          status: "Active",
        },
      });
      // The list uses the custom queryKey below (not the orval-generated one), so
      // invalidate that exact key — otherwise the new value never refetches.
      queryClient.invalidateQueries({
        queryKey: ["listAttributeValues", attributeKeyId],
      });
      onChange(res.id, res.value);
      setInputValue("");
      setOpen(false);
      toast({ title: `Added value '${res.value}'` });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        toast({
          title: "Already exists",
          description: `Value '${inputValue}' already exists for this attribute.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to add attribute value",
          variant: "destructive",
        });
      }
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled || attributeKeyId == null}
        >
          <span className={cn(!selectedValue && !valueText && "text-muted-foreground")}>
            {selectedValue?.value ?? (valueText && valueText.trim().length > 0 ? valueText : placeholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type to add…"
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {attributeKeyId == null
                ? "Select an attribute key first."
                : "No values found."}
            </CommandEmpty>
            <CommandGroup>
              {filteredValues.map((v) => (
                <CommandItem
                  key={v.id}
                  value={String(v.id)}
                  onSelect={() => {
                    onChange(v.id, v.value);
                    setInputValue("");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === v.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {v.value}
                </CommandItem>
              ))}
            </CommandGroup>
            {showAddNew && (
              <CommandGroup>
                <CommandItem
                  value={`__create_${inputValue}`}
                  onSelect={handleAddNew}
                  disabled={createMutation.isPending}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add &ldquo;{inputValue.trim()}&rdquo; as new value
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
